import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonType,
  Drawer,
  Row,
  Col
} from 'syngenta-digital-cropwise-react-ui-kit';

import CloseIcon from 'assets/close.svg';
import PlusIcon from 'assets/plus_green.svg';
import ChevronLeft from 'assets/chevron_left.svg';
import FilterBySelectInput from 'components/FilterBySelectInput';
import useSessionStorage from 'hooks/useSessionStorage';
import {
  LIST_FILTER_OPTIONS,
  TYPE_FILTER_ADD_OPTIONS
} from 'screens/ProductList/helpers/tableConstants';
import { ProductListContext } from 'screens/ProductList/context/ProductListProvider';
import ProductSearch from '../AddProductSearch';
import ProductSearchList from './components/ProductSearchList';

const EXPANDED_SIDEBAR_WIDTH = 256;

export const ADD_PRODUCT_DRAWER_FILTERS_SESSION_KEY =
  'cropwise-financials-add-product-drawer-filters';

const AddProductDrawer = ({
  zIndex,
  visible,
  mask,
  submitButtonText,
  submitButtonType,
  submitButtonDisabled,
  selectedProductsList,
  setSelectedProductsList,
  handleOnAddCustomProduct,
  handleOnEdit,
  handleAddProductsSubmit,
  onClose,
  showProductList,
  hideBackButton,
  myProductsReloadTrigger
}) => {
  const { storedValue: rawFilters, setValue: setFilters } = useSessionStorage(
    ADD_PRODUCT_DRAWER_FILTERS_SESSION_KEY,
    {
      typeFilters: [],
      listFilters: []
    }
  );
  const typeFilters = Array.isArray(rawFilters?.typeFilters)
    ? rawFilters.typeFilters
    : [];
  const listFilters = Array.isArray(rawFilters?.listFilters)
    ? rawFilters.listFilters
    : [];
  const setTypeFilters = next =>
    setFilters({ ...rawFilters, typeFilters: next });
  const setListFilters = next =>
    setFilters({ ...rawFilters, listFilters: next });
  const { setSearchText } = useContext(ProductListContext);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        const input = document.querySelector('input#product-search');
        if (input) input.focus();
      }, 0);
    } else {
      setSearchText('');
    }
  }, [visible, setSearchText]);

  return (
    <Drawer
      zIndex={zIndex}
      className="product-drawer"
      open={visible}
      placement="right"
      closable={false}
      mask={mask} // When false, caller manages dimming externally to avoid flicker during drawer swaps
      maskClosable
      onClose={onClose}
      width={`calc(100% - ${EXPANDED_SIDEBAR_WIDTH}px)`}
      footer={
        <Row justify={hideBackButton ? 'end' : 'space-between'}>
          {hideBackButton ? null : (
            <Button
              type={ButtonType.outline}
              size="large"
              className="mr-10px"
              onClick={onClose}
              icon={<img alt="chevron left" src={ChevronLeft} />}
            >
              Back
            </Button>
          )}

          <Button
            type={submitButtonType}
            size="large"
            onClick={handleAddProductsSubmit}
            disabled={submitButtonDisabled}
          >
            {submitButtonText}
          </Button>
        </Row>
      }
    >
      <div className="h-full flex flex-col">
        <div className="pt-4 pr-4 pb-5 pl-8">
          <Row justify="end">
            <button type="button" aria-label="Close" onClick={onClose}>
              <img alt="close" src={CloseIcon} />
            </button>
          </Row>
          <div className="ml-2 mb-5 text-xl font-semibold">
            {showProductList
              ? 'Select Products'
              : 'Add Products to Product List'}
          </div>
          <Row gutter={16} className="pr-4">
            <Col flex="auto">
              <ProductSearch />
            </Col>
            <Col>
              <FilterBySelectInput
                options={TYPE_FILTER_ADD_OPTIONS}
                value={typeFilters}
                onChange={setTypeFilters}
                placeholder="Filter by Type"
                style={{ minWidth: 246 }}
              />
            </Col>
            {showProductList && (
              <Col>
                <FilterBySelectInput
                  options={LIST_FILTER_OPTIONS}
                  value={listFilters}
                  onChange={setListFilters}
                  placeholder="Filter by List"
                  style={{ minWidth: 246 }}
                />
              </Col>
            )}
            <Col>
              <Button
                ghost
                type={ButtonType.primary}
                className="px-3"
                onClick={handleOnAddCustomProduct}
              >
                <Row style={{ width: 120 }}>
                  <img alt="plus" src={PlusIcon} className="mr-1" />
                  <span>Create Custom</span>
                </Row>
              </Button>
            </Col>
          </Row>
        </div>
        <ProductSearchList
          showProductList={showProductList}
          selectedProductsList={selectedProductsList}
          setSelectedProductsList={setSelectedProductsList}
          handleOnEdit={handleOnEdit}
          typeFilters={typeFilters}
          listFilters={listFilters}
          myProductsReloadTrigger={myProductsReloadTrigger}
        />
      </div>
    </Drawer>
  );
};

AddProductDrawer.defaultProps = {
  zIndex: 1000,
  mask: undefined,
  submitButtonText: 'Add to Product List',
  submitButtonType: 'primary',
  submitButtonDisabled: false,
  showProductList: false,
  hideBackButton: false,
  myProductsReloadTrigger: 0
};

AddProductDrawer.propTypes = {
  zIndex: PropTypes.number,
  mask: PropTypes.bool,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  submitButtonText: PropTypes.string,
  submitButtonType: PropTypes.string,
  submitButtonDisabled: PropTypes.bool,
  selectedProductsList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      manufacturer: PropTypes.string,
      producttype: PropTypes.string,
      isInUse: PropTypes.bool
    })
  ).isRequired,
  setSelectedProductsList: PropTypes.func.isRequired,
  handleOnEdit: PropTypes.func.isRequired,
  handleAddProductsSubmit: PropTypes.func.isRequired,
  handleOnAddCustomProduct: PropTypes.func.isRequired,
  showProductList: PropTypes.bool,
  hideBackButton: PropTypes.bool,
  myProductsReloadTrigger: PropTypes.number
};

export default AddProductDrawer;
